
import { Routes, Route, Navigate } from "react-router-dom";
import { LoginProvider, LoginContext } from "./LoginContext";
import { MessagesProvider } from "./MessagesContext";
import { useEffect, useContext } from "react";
import csrfService from "./utils/csrfService";
import {
  Header,
  Footer,
  HelpChatButton,
  MessageNotification,
  PaymentRedirectHandler,
  ScrollToTop
} from "./components";
import TotpGuard from "./components/TotpGuard";
import AppVersionGuard from "./components/AppVersionGuard";
import {
  PageLanding,
  PageLogin,
  UploadPage,
  PageModelViewer,
  PageOrthesisEditor,
  PageSetPassword,
  Pakete,
  DownloadsPakete,
  PaymentSuccess,
  PaymentCancel,
  Projects,
  ProjectDetails,
  Project2FAVerify,
  AdminAccountAction,
  AdminAssignRoles,
  AdminDashboard,
  ProfileLayout,
  ProfileDetails,
  ProfileBilling,
  ProfileSubscription,
  ProfileMessages,
  ProfileAccessibility,
  PageChangePassword,
  ProfileNextcloud
} from "./pages";

// import "./stylesheets/Layout.css"; // Replaced by Tailwind
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { useLanguageDirection } from "./utils/useLanguageDirection";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AccessibilityProvider } from "./contexts/AccessibilityContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { NotificationContainer } from "./components/Notification";
import "./styles/accessibility.css";
import CountdownGate from "./components/CountdownGate";

// Route guard: redirects to landing page when not logged in
// CountdownGate blocks access in live mode until the launch date
const ProtectedRoute = ({ children }) => {
  const { loginInfo, loading } = useContext(LoginContext);
  if (loading) return null;
  if (!loginInfo) return <Navigate to="/" replace />;
  return <CountdownGate>{children}</CountdownGate>;
};

function App() {
  const { direction } = useLanguageDirection();

  // 🔒 Initialize CSRF protection when app loads
  useEffect(() => {
    csrfService.fetchToken().catch(err => {
      console.error('Failed to initialize CSRF protection:', err);
    });
  }, []);

  return (
    <AccessibilityProvider>
      <ThemeProvider>
        <NotificationProvider>
          <LoginProvider>
            <MessagesProvider>
            <I18nextProvider i18n={i18n}>
              <div className={`flex flex-col min-h-screen overflow-x-hidden ${direction}`}>
                <ScrollToTop />
                <PaymentRedirectHandler />
                <AppVersionGuard />
                <Header />
                <main className="flex-1 flex flex-col pt-20">
                  <div className="flex-1 flex flex-col">
                    <Routes>
                      {/* Public routes */}
                      <Route path='/' element={<PageLanding />} />
                      <Route path='/login' element={<PageLogin />} />
                      <Route path='/set-password' element={<PageSetPassword />} />
                      <Route path='/admin/account-action' element={<AdminAccountAction />} />

                      {/* Protected routes - redirect to / when not logged in */}
<Route path='/upload' element={<ProtectedRoute><UploadPage /></ProtectedRoute>} />
                      <Route path='/modelviewer' element={<ProtectedRoute><PageModelViewer /></ProtectedRoute>} />
                      {/* Orthesis editor is hidden on the live build —
                          currently in TEST PHASE, deliberately excluded from
                          video-3d-scan.3d-medico.de until pattern geometry
                          + CSG robustness are signed off (see
                          docs/LIVE-FEATURE-FLAGS.md). Same `REACT_APP_APP_MODE`
                          flag drives Header.jsx so nav + direct URL stay in sync. */}
                      {process.env.REACT_APP_APP_MODE !== 'live' && (
                        <Route path='/orthesis-editor' element={<ProtectedRoute><PageOrthesisEditor /></ProtectedRoute>} />
                      )}

                      <Route path='/profile' element={<ProtectedRoute><ProfileLayout /></ProtectedRoute>}>
                        <Route index element={<Navigate to="details" replace />} />
                        <Route path='details' element={<ProfileDetails />} />
                        <Route path='change-password' element={<PageChangePassword />} />
                        <Route path='subscription' element={<ProfileSubscription />} />
                        <Route path='billing' element={<ProfileBilling />} />
                        <Route path='healthcloud' element={<ProfileNextcloud />} />
                        <Route path='messages' element={<ProfileMessages />} />
                        <Route path='accessibility' element={<ProfileAccessibility />} />
                      </Route>

                      <Route path='/pakete' element={<ProtectedRoute><Pakete /></ProtectedRoute>} />
                      <Route path='/downloadspakete' element={<ProtectedRoute><DownloadsPakete /></ProtectedRoute>} />
                      <Route path='/payment/success' element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
                      <Route path='/payment/cancel' element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
                      <Route path='/projects' element={<ProtectedRoute><TotpGuard><Projects /></TotpGuard></ProtectedRoute>} />
                      <Route path='/projects/verify' element={<ProtectedRoute><Project2FAVerify /></ProtectedRoute>} />
                      <Route path='/projects/:id' element={<ProtectedRoute><TotpGuard><ProjectDetails /></TotpGuard></ProtectedRoute>} />

                      {/* Admin routes */}
                      <Route path='/assign-roles/:userId' element={<ProtectedRoute><AdminAssignRoles /></ProtectedRoute>} />
                      <Route path='/admin/dashboard' element={<ProtectedRoute><TotpGuard><AdminDashboard /></TotpGuard></ProtectedRoute>} />
                    </Routes>
                  </div>
                </main>
                <HelpChatButton />
                <MessageNotification />
                <Footer />
                <NotificationContainer />
                {/*<AccessibilityMenu />*/}
              </div>
            </I18nextProvider>
          </MessagesProvider>
        </LoginProvider>
        </NotificationProvider>
      </ThemeProvider>
    </AccessibilityProvider>
  );
}

export default App;
